// src/pages/Pagos.jsx
import React, { useMemo, useRef, useState } from "react";
import "./Pagos.css";
import { authFetch } from "../utils/authFetch";
import {
  AccionFila, Boton, Confirmar, EncabezadoPagina, Iconos, Insignia,
} from "../componentes/base";

/* =========================================================
   Las otras dos tablas

   `pagos` tiene su propia pantalla desde siempre y se queda como está.
   `caja` e `instalaciones` se leen y se corrigen por /api/registros, que
   devuelve las columnas que tiene la tabla de verdad en vez de una lista
   escrita a mano aquí: así la pantalla no se rompe si la tabla cambia.
========================================================= */
const MODOS = [
  { id: "pagos", texto: "Pagos" },
  { id: "caja", texto: "Caja" },
  { id: "instalaciones", texto: "Instalaciones" },
];

const FORMAS = ["oficina", "banamex", "bancomer", "otro"];

// Nombre en pantalla de cada columna. Lo que no esté aquí se muestra tal
// cual, sin guiones bajos: una columna nueva se ve fea, pero se ve.
const ETIQUETAS = {
  id: "ID",
  cliente_id: "Cliente",
  concepto: "Concepto",
  monto: "Monto",
  monto_total: "Total",
  monto_mensual: "Mensual",
  meses: "Meses",
  meses_restantes: "Restantes",
  pagado_completo: "Liquidada",
  pagado: "Pagado",
  forma_pago: "Forma",
  referencia: "Referencia",
  fecha: "Fecha",
  fecha_inicio: "Inicio",
  fecha_pago_hora: "Fecha pago",
  creado: "Creado",
  mes: "Mes",
  anio: "Año",
};

function etiquetaCol(c) {
  return ETIQUETAS[c] || c.replace(/_/g, " ");
}

// La misma regla que usa el backend: el tipo sale del nombre de la
// columna, porque las seis tablas siguen la misma nomenclatura.
function tipoDe(c) {
  const n = String(c).toLowerCase();
  if (n.startsWith("monto")) return "monto";
  if (n.startsWith("meses") || n === "mes" || n === "anio") return "entero";
  if (n.startsWith("pagado") || n === "activo") return "bool";
  if (n.startsWith("fecha") || n === "creado") return "fecha";
  if (n === "forma_pago") return "forma";
  return "texto";
}

/* El backend manda las fechas como texto "YYYY-MM-DD HH:MM:SS" y aquí se
   parten a mano. Pasarlas por new Date() les aplicaría el huso horario
   del navegador y una hora de pago se movería sola. */
function fmtFechaTexto(v) {
  if (!v) return "-";
  const s = String(v);
  const [dia, hora = ""] = s.split(" ");
  const [y, m, d] = dia.split("-");
  if (!y || !m || !d) return s;
  const hhmm = hora.slice(0, 5);
  return hhmm && hhmm !== "00:00" ? `${d}/${m}/${y} ${hhmm}` : `${d}/${m}/${y}`;
}

function paraInputFecha(v) {
  // "2026-09-08 14:30:00" -> "2026-09-08T14:30"
  return String(v || "").slice(0, 16).replace(" ", "T");
}

export default function Pagos() {
  const API_URL = "/api/pagos";

  // qué tabla se está viendo
  const [modo, setModo] = useState("pagos");
  const [servicio, setServicio] = useState("fibra");

  // filtros
  const [clienteId, setClienteId] = useState("");
  const [anio, setAnio] = useState("");
  const [mes, setMes] = useState("");
  const [forma, setForma] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  // inline edit
  const [editando, setEditando] = useState({ id: null, campo: "", valor: "" });

  // modos caja / instalaciones: la tabla llega descrita desde el backend
  const [tabla, setTabla] = useState({ columnas: [], editables: [], filas: [] });
  const [editReg, setEditReg] = useState({ id: null, campo: "", valor: "", original: "" });

  // Fila que espera confirmación para borrarse (las tres tablas comparten
  // el mismo diálogo).
  const [porBorrar, setPorBorrar] = useState(null);

  // evitar respuestas viejas
  const reqIdRef = useRef(0);

  // La tabla real detrás del modo. Fibra y WiFi son tablas distintas y
  // sus ids no tienen nada que ver entre sí.
  const origen =
    modo === "pagos" ? "pagos" : servicio === "wifi" ? `${modo}_wifi` : modo;

  const puedeForma = modo !== "instalaciones"; // instalaciones no cobra: no tiene forma

  function showToast(mensaje, tipo = "info") {
    window.dispatchEvent(new CustomEvent("toast", { detail: { mensaje, tipo } }));
  }

  const filtrosActivos = useMemo(() => {
    return {
      cliente_id: clienteId.trim(),
      anio: String(anio || "").trim(),
      mes: String(mes || "").trim(),
      forma_pago: String(forma || "").trim(),
    };
  }, [clienteId, anio, mes, forma]);

  // ✅ años para select (sin librerías)
  const years = useMemo(() => {
    const current = new Date().getFullYear();
    const startYear = 2016; // cámbialo si quieres
    const arr = [];
    for (let y = current; y >= startYear; y--) arr.push(String(y));
    return arr;
  }, []);

  function buildParams({ limit, offset }) {
    const params = new URLSearchParams();

    params.set("cliente_id", filtrosActivos.cliente_id);
    if (filtrosActivos.anio) params.set("anio", filtrosActivos.anio);
    if (filtrosActivos.mes) params.set("mes", filtrosActivos.mes);
    if (filtrosActivos.forma_pago) params.set("forma_pago", filtrosActivos.forma_pago);

    params.set("limit", String(limit));
    params.set("offset", String(offset));
    return params;
  }

  async function fetchChunk({ limit, offset, myReqId }) {
    const url = `${API_URL}?${buildParams({ limit, offset }).toString()}`;
    const res = await authFetch(url);
    const data = await res.json();

    if (myReqId !== reqIdRef.current) return { ok: false, ignore: true, pagos: [] };

    if (!res.ok || !data.ok) {
      const msg = data?.mensaje || data?.error || "Error consultando pagos";
      return { ok: false, mensaje: msg, pagos: [] };
    }

    return { ok: true, pagos: Array.isArray(data.pagos) ? data.pagos : [] };
  }

  async function buscarTodos() {
    const cid = filtrosActivos.cliente_id;
    if (!cid) {
      showToast("Escribe un Cliente ID para buscar.", "info");
      return;
    }

    const myReqId = ++reqIdRef.current;
    setLoading(true);
    setError("");
    setRows([]);
    setStatus("Consultando (trayendo todo)...");

    const limit = 5000;
    let offset = 0;
    let all = [];

    try {
      while (true) {
        setStatus(`Consultando... (van ${all.length})`);

        const r = await fetchChunk({ limit, offset, myReqId });
        if (r.ignore) return;

        if (!r.ok) {
          setError(r.mensaje || "Error consultando pagos");
          showToast(r.mensaje || "Error consultando pagos", "error");
          setLoading(false);
          setStatus("");
          return;
        }

        const chunk = r.pagos || [];
        all = all.concat(chunk);

        if (chunk.length < limit) break;
        offset += limit;

        if (offset > 2000000) break; // seguridad
      }

      setRows(all);
      setStatus(`Listo: ${all.length} pago(s) del cliente ${cid}.`);
      showToast(`Encontrados: ${all.length}`, "success");
      setLoading(false);
    } catch (e) {
      if (myReqId !== reqIdRef.current) return;
      setError("Error de red consultando pagos");
      showToast("Error de red consultando pagos", "error");
      setLoading(false);
      setStatus("");
    }
  }

  function limpiar() {
    reqIdRef.current++;
    setRows([]);
    setTabla({ columnas: [], editables: [], filas: [] });
    setError("");
    setStatus("");
    setMes("");
    setAnio("");
    setForma("");
    setEditando({ id: null, campo: "", valor: "" });
    setEditReg({ id: null, campo: "", valor: "", original: "" });
  }

  function cambiarModo(id) {
    if (id === modo) return;
    setModo(id);
    // Lo que hay en pantalla es de la tabla anterior: no se hereda.
    reqIdRef.current++;
    setRows([]);
    setTabla({ columnas: [], editables: [], filas: [] });
    setError("");
    setStatus("");
    setEditando({ id: null, campo: "", valor: "" });
    setEditReg({ id: null, campo: "", valor: "", original: "" });
    if (id === "instalaciones") setForma("");
  }

  function cambiarServicio(s) {
    if (s === servicio) return;
    setServicio(s);
    setTabla({ columnas: [], editables: [], filas: [] });
    setStatus("");
  }

  /* -------------------------------------------------------
     CAJA E INSTALACIONES
  ------------------------------------------------------- */
  async function buscarRegistros() {
    const cid = filtrosActivos.cliente_id;
    if (!cid && !filtrosActivos.anio) {
      showToast("Pon un Cliente ID o un año para buscar.", "info");
      return;
    }

    const myReqId = ++reqIdRef.current;
    setLoading(true);
    setError("");
    setTabla({ columnas: [], editables: [], filas: [] });
    setStatus("Consultando...");

    try {
      const params = new URLSearchParams();
      if (cid) params.set("cliente_id", cid);
      if (filtrosActivos.anio) params.set("anio", filtrosActivos.anio);
      if (filtrosActivos.mes) params.set("mes", filtrosActivos.mes);
      if (puedeForma && filtrosActivos.forma_pago) {
        params.set("forma_pago", filtrosActivos.forma_pago);
      }

      const res = await authFetch(`/api/registros/${origen}?${params.toString()}`);
      const data = await res.json();

      if (myReqId !== reqIdRef.current) return;

      if (!res.ok || !data.ok) {
        const msg = data?.mensaje || data?.error || "Error consultando";
        setError(msg);
        showToast(msg, "error");
        setStatus("");
        return;
      }

      setTabla({
        columnas: data.columnas || [],
        editables: data.editables || [],
        filas: data.filas || [],
      });
      const n = (data.filas || []).length;
      setStatus(`Listo: ${n} registro(s).`);
      showToast(`Encontrados: ${n}`, "success");
    } catch (e) {
      if (myReqId !== reqIdRef.current) return;
      setError("Error de red consultando");
      showToast("Error de red consultando", "error");
      setStatus("");
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  }

  function buscar() {
    if (modo === "pagos") return buscarTodos();
    return buscarRegistros();
  }

  const esEditable = (c) => tabla.editables.includes(c);
  const editandoReg = (id, campo) => editReg.id === id && editReg.campo === campo;

  function abrirReg(fila, campo) {
    if (!esEditable(campo)) return;
    const crudo =
      tipoDe(campo) === "fecha" ? paraInputFecha(fila[campo]) : fila[campo];
    const valor = crudo === null || crudo === undefined ? "" : String(crudo);
    setEditReg({ id: fila.id, campo, valor, original: valor });
  }

  const cancelarReg = () => setEditReg({ id: null, campo: "", valor: "", original: "" });

  async function guardarReg() {
    const { id, campo, valor, original } = editReg;
    if (!id || !campo) return;

    cancelarReg();
    if (valor === original) return; // no cambió nada: no se molesta al servidor

    setSavingId(id);
    try {
      const res = await authFetch(`/api/registros/${origen}/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ campo, valor }),
      });
      const data = await res.json();

      if (!res.ok || !data.ok) {
        showToast(data?.mensaje || "No se pudo guardar", "error");
        return;
      }

      // `cambios` trae también lo que el backend arrastró por coherencia:
      // poner meses_restantes en 0 marca la instalación como liquidada.
      setTabla((t) => ({
        ...t,
        filas: t.filas.map((f) => (f.id === id ? { ...f, ...(data.cambios || {}) } : f)),
      }));
      showToast("Actualizado", "success");
    } catch (e) {
      showToast("Error de red guardando", "error");
    } finally {
      setSavingId(null);
    }
  }

  function pedirBorrarReg(fila) {
    const monto = fila.monto ?? fila.monto_total;

    // Un cobro de instalación descontó un mes del plan del cliente al
    // cobrarse. Borrarlo aquí no lo devuelve: eso se arregla en la
    // pestaña Instalaciones, subiendo los meses restantes a mano.
    const deInstalacion = String(fila.referencia || "").toLowerCase() === "instalacion";

    setPorBorrar({
      tipo: "registro",
      fila,
      titulo: `Borrar el registro #${fila.id}`,
      mensaje:
        monto === null || monto === undefined
          ? "No se puede deshacer."
          : `Son ${fmtMoney(monto)} y no se puede deshacer.`,
      aviso: deInstalacion
        ? "Es un cobro de instalación. Si era a meses, los meses restantes del cliente NO se recalculan: revísalos en la pestaña Instalaciones."
        : null,
    });
  }

  async function borrarReg(fila) {
    setDeletingId(fila.id);
    try {
      const res = await authFetch(`/api/registros/${origen}/${fila.id}`, {
        method: "DELETE",
      });
      const data = await res.json();

      if (!res.ok || !data.ok) {
        showToast(data?.mensaje || "No se pudo borrar", "error");
        return;
      }

      setTabla((t) => ({ ...t, filas: t.filas.filter((f) => f.id !== fila.id) }));
      showToast(data.mensaje || "Registro borrado", "success");
    } catch (e) {
      showToast("Error de red borrando", "error");
    } finally {
      setDeletingId(null);
    }
  }

  // El diálogo es uno solo para las tres tablas; aquí se decide a quién
  // le toca hacer el trabajo cuando el usuario dice que sí.
  async function confirmarBorrado() {
    const pendiente = porBorrar;
    if (!pendiente) return;
    setPorBorrar(null);
    if (pendiente.tipo === "pago") await borrarPago(pendiente.fila);
    else await borrarReg(pendiente.fila);
  }

  function verValor(campo, v) {
    if (v === null || v === undefined || v === "") return "-";
    switch (tipoDe(campo)) {
      case "monto":
        return fmtMoney(v);
      case "bool":
        return Number(v) === 1 ? "Sí" : "No";
      case "fecha":
        return fmtFechaTexto(v);
      default:
        return String(v);
    }
  }

  function inputReg(campo) {
    const comun = {
      autoFocus: true,
      className: "cell-input",
      value: editReg.valor,
      onBlur: guardarReg,
      onKeyDown: (e) => {
        if (e.key === "Enter") guardarReg();
        if (e.key === "Escape") cancelarReg();
      },
    };
    const set = (v) => setEditReg((x) => ({ ...x, valor: v }));

    switch (tipoDe(campo)) {
      case "bool":
        return (
          <select {...comun} onChange={(e) => set(e.target.value)}>
            <option value="1">Sí</option>
            <option value="0">No</option>
          </select>
        );
      case "forma":
        return (
          <select {...comun} onChange={(e) => set(e.target.value)}>
            {FORMAS.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        );
      case "fecha":
        return (
          <input {...comun} type="datetime-local" onChange={(e) => set(e.target.value)} />
        );
      case "entero":
        return (
          <input
            {...comun}
            inputMode="numeric"
            onChange={(e) => set(e.target.value.replace(/\D/g, "").slice(0, 6))}
          />
        );
      case "monto":
        return <input {...comun} inputMode="decimal" onChange={(e) => set(e.target.value)} />;
      default:
        return <input {...comun} onChange={(e) => set(e.target.value)} />;
    }
  }

  function fmtMoney(n) {
    const num = Number(n || 0);
    return num.toLocaleString("es-MX", { style: "currency", currency: "MXN" });
  }

  function fmtFecha(v) {
    if (!v) return "-";
    try {
      return new Date(v).toLocaleString();
    } catch {
      return String(v);
    }
  }

  function toDateTimeLocalValue(v) {
    if (!v) return "";
    try {
      const d = new Date(v);
      const pad = (n) => String(n).padStart(2, "0");
      const yyyy = d.getFullYear();
      const mm = pad(d.getMonth() + 1);
      const dd = pad(d.getDate());
      const hh = pad(d.getHours());
      const mi = pad(d.getMinutes());
      return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
    } catch {
      return "";
    }
  }

  function activarEdicion(pago, campo) {
    let valor = pago[campo];
    if (campo === "fecha_pago_hora") valor = toDateTimeLocalValue(pago.fecha_pago_hora);

    setEditando({
      id: pago.id,
      campo,
      valor: valor === null || valor === undefined ? "" : String(valor),
    });
  }

  function cancelarEdicion() {
    setEditando({ id: null, campo: "", valor: "" });
  }

  async function guardarEdicion() {
    if (!editando.id || !editando.campo) return;

    setSavingId(editando.id);
    try {
      const res = await authFetch(`${API_URL}/editar`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: editando.id,
          campo: editando.campo,
          valor: editando.valor,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        const msg = data?.mensaje || data?.error || "No se pudo guardar";
        showToast(msg, "error");
        setSavingId(null);
        return;
      }

      setRows((prev) =>
        prev.map((p) => (p.id === editando.id ? { ...p, [editando.campo]: editando.valor } : p))
      );

      showToast("Actualizado", "success");
      cancelarEdicion();
      setSavingId(null);
    } catch (e) {
      showToast("Error de red guardando", "error");
      setSavingId(null);
    }
  }

  function pedirBorrarPago(pago) {
    if (!pago?.id) return;
    setPorBorrar({
      tipo: "pago",
      fila: pago,
      titulo: `Borrar el pago #${pago.id}`,
      mensaje: `${fmtMoney(pago.monto)} del cliente ${pago.cliente_id}. No se puede deshacer.`,
      aviso: null,
    });
  }

  async function borrarPago(pago) {
    setDeletingId(pago.id);
    try {
      const res = await authFetch(`${API_URL}/${pago.id}`, { method: "DELETE" });
      const data = await res.json();

      if (!res.ok || !data.ok) {
        const msg = data?.mensaje || data?.error || "No se pudo borrar el pago";
        showToast(msg, "error");
        setDeletingId(null);
        return;
      }

      setRows((prev) => prev.filter((x) => x.id !== pago.id));
      showToast("Pago borrado", "success");
      setDeletingId(null);
    } catch (e) {
      showToast("Error de red borrando pago", "error");
      setDeletingId(null);
    }
  }

  const isEditing = (id, campo) => editando.id === id && editando.campo === campo;

  const cuantos = modo === "pagos" ? rows.length : tabla.filas.length;
  const tituloTabla =
    modo === "pagos"
      ? "Pagos encontrados"
      : modo === "caja"
      ? `Caja ${servicio === "wifi" ? "WiFi" : "fibra"}`
      : `Instalaciones ${servicio === "wifi" ? "WiFi" : "fibra"}`;

  // El modo baja al DOM porque el color de toda la página cuelga de él.
  return (
    <div className="pagos-page" data-modo={modo}>
      <EncabezadoPagina
        icono={Iconos.pagos}
        titulo="Pagos"
        resumen={
          modo === "pagos"
            ? "Consulta por cliente, año, mes o forma de pago. Doble clic en una celda para editarla."
            : modo === "caja"
            ? "Movimientos de caja del cliente. Doble clic para corregir, la papelera para borrar."
            : "Instalaciones a meses. Doble clic para corregir los meses restantes o el monto."
        }
      />

      {/* Qué tabla se está viendo */}
      <div className="pagos-segment">
        {MODOS.map((m) => (
          <button
            key={m.id}
            type="button"
            className={`pagos-seg-btn ${modo === m.id ? "active" : ""}`}
            disabled={loading}
            onClick={() => cambiarModo(m.id)}
          >
            {m.texto}
          </button>
        ))}

        {/* Fibra y WiFi guardan en tablas distintas; pagos aún no. */}
        {modo !== "pagos" && (
          <div className="pagos-servicio">
            {["fibra", "wifi"].map((s) => (
              <button
                key={s}
                type="button"
                className={`pagos-seg-btn chico ${servicio === s ? "active" : ""}`}
                disabled={loading}
                onClick={() => cambiarServicio(s)}
              >
                {s === "fibra" ? "Fibra" : "WiFi"}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* HERO integrado tipo Cortes */}
      <div className="pagos-hero">
        {/* TOOLBAR (pills) */}
        <div className="pagos-toolbar">
          <div className="pagos-pill">
            <label>Cliente ID</label>
            <input
              className="pagos-inp"
              placeholder="Ej: 123"
              value={clienteId}
              onChange={(e) => setClienteId(e.target.value.replace(/\D/g, ""))}
            />
          </div>

          <div className="pagos-pill">
            <label>Año</label>
            <select className="pagos-inp" value={anio} onChange={(e) => setAnio(e.target.value)}>
              <option value="">Todos</option>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          <div className="pagos-pill">
            <label>Mes</label>
            <select className="pagos-inp" value={mes} onChange={(e) => setMes(e.target.value)}>
              <option value="">Todos</option>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={String(m)}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          <div className="pagos-pill">
            <label>Forma pago</label>
            <select
              className="pagos-inp"
              value={forma}
              disabled={!puedeForma}
              title={puedeForma ? undefined : "Las instalaciones no guardan forma de pago"}
              onChange={(e) => setForma(e.target.value)}
            >
              <option value="">Todas</option>
              <option value="oficina">oficina</option>
              <option value="banamex">banamex</option>
              <option value="bancomer">bancomer</option>
              <option value="otro">otro</option>
            </select>
          </div>

          <div className="pagos-toolbar-actions">
            <Boton
              icono={Iconos.buscar}
              cargando={loading}
              onClick={buscar}
              type="button"
            >
              Buscar
            </Boton>

            <Boton
              variante="secundario"
              icono={Iconos.cerrar}
              disabled={loading}
              onClick={limpiar}
              type="button"
            >
              Limpiar
            </Boton>
          </div>
        </div>

        {/* “Resultados” flotando, estilo Cortes */}
        <div className="pagos-stats">
          <div className="pagos-stat stat-cyan">
            <div className="stat-label">Resultados</div>
            <div className="stat-value">{cuantos.toLocaleString("es-MX")}</div>
          </div>

          <div className="pagos-stat stat-slate">
            <div className="stat-label">Estado</div>
            <div className="stat-value small">{status || (loading ? "Consultando..." : "—")}</div>
          </div>
        </div>
      </div>

      {error && <div className="pagos-error">{error}</div>}

      {/* TABLA (card tipo Cortes) */}
      <div className="pagos-card">
        <div className="pagos-card-header">
          <h2>
            <Iconos.documento size={17} strokeWidth={1.8} />
            {tituloTabla}
          </h2>
          <Insignia
            variante="neutra"
            texto={`${cuantos.toLocaleString("es-MX")} encontrados`}
          />
        </div>

        {modo === "pagos" ? (
        <div className="pagos-table-wrapper">
          <table className="pagos-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Cliente</th>
                <th>Mes</th>
                <th>Año</th>
                <th>Fecha pago</th>
                <th>Monto</th>
                <th>Forma</th>
                <th>Referencia</th>
                <th>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="empty">
                    Consultando pagos...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="empty">
                    Sin resultados
                  </td>
                </tr>
              ) : (
                rows.map((p) => (
                  <tr key={p.id}>
                    <td className="td-strong">{p.id}</td>
                    <td>{p.cliente_id}</td>

                    <td onDoubleClick={() => activarEdicion(p, "mes")}>
                      {isEditing(p.id, "mes") ? (
                        <input
                          autoFocus
                          className="cell-input"
                          value={editando.valor}
                          onChange={(e) =>
                            setEditando({
                              ...editando,
                              valor: e.target.value.replace(/\D/g, "").slice(0, 2),
                            })
                          }
                          onBlur={guardarEdicion}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") guardarEdicion();
                            if (e.key === "Escape") cancelarEdicion();
                          }}
                        />
                      ) : (
                        p.mes
                      )}
                    </td>

                    <td onDoubleClick={() => activarEdicion(p, "anio")}>
                      {isEditing(p.id, "anio") ? (
                        <input
                          autoFocus
                          className="cell-input"
                          value={editando.valor}
                          onChange={(e) =>
                            setEditando({
                              ...editando,
                              valor: e.target.value.replace(/\D/g, "").slice(0, 4),
                            })
                          }
                          onBlur={guardarEdicion}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") guardarEdicion();
                            if (e.key === "Escape") cancelarEdicion();
                          }}
                        />
                      ) : (
                        p.anio
                      )}
                    </td>

                    <td onDoubleClick={() => activarEdicion(p, "fecha_pago_hora")}>
                      {isEditing(p.id, "fecha_pago_hora") ? (
                        <input
                          autoFocus
                          className="cell-input"
                          type="datetime-local"
                          value={editando.valor}
                          onChange={(e) => setEditando({ ...editando, valor: e.target.value })}
                          onBlur={guardarEdicion}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") guardarEdicion();
                            if (e.key === "Escape") cancelarEdicion();
                          }}
                        />
                      ) : (
                        fmtFecha(p.fecha_pago_hora)
                      )}
                    </td>

                    <td className="money" onDoubleClick={() => activarEdicion(p, "monto")}>
                      {isEditing(p.id, "monto") ? (
                        <input
                          autoFocus
                          className="cell-input"
                          value={editando.valor}
                          onChange={(e) => setEditando({ ...editando, valor: e.target.value })}
                          onBlur={guardarEdicion}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") guardarEdicion();
                            if (e.key === "Escape") cancelarEdicion();
                          }}
                        />
                      ) : (
                        fmtMoney(p.monto)
                      )}
                    </td>

                    <td onDoubleClick={() => activarEdicion(p, "forma_pago")}>
                      {isEditing(p.id, "forma_pago") ? (
                        <select
                          autoFocus
                          className="cell-input"
                          value={editando.valor}
                          onChange={(e) => setEditando({ ...editando, valor: e.target.value })}
                          onBlur={guardarEdicion}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") cancelarEdicion();
                          }}
                        >
                          <option value="oficina">oficina</option>
                          <option value="banamex">banamex</option>
                          <option value="bancomer">bancomer</option>
                          <option value="otro">otro</option>
                        </select>
                      ) : (
                        p.forma_pago
                      )}
                    </td>

                    <td onDoubleClick={() => activarEdicion(p, "referencia")}>
                      {isEditing(p.id, "referencia") ? (
                        <input
                          autoFocus
                          className="cell-input"
                          value={editando.valor}
                          onChange={(e) => setEditando({ ...editando, valor: e.target.value })}
                          onBlur={guardarEdicion}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") guardarEdicion();
                            if (e.key === "Escape") cancelarEdicion();
                          }}
                        />
                      ) : (
                        p.referencia || "-"
                      )}
                    </td>

                    <td>
                      <AccionFila
                        icono={deletingId === p.id ? Iconos.cargando : Iconos.eliminar}
                        titulo={`Borrar pago #${p.id}`}
                        peligro
                        type="button"
                        disabled={loading || savingId === p.id || deletingId === p.id}
                        onClick={() => pedirBorrarPago(p)}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        ) : (
          /* Caja e instalaciones: las columnas son las que dijo el backend.
             El data-label es lo que se ve como título en el teléfono, donde
             la tabla se convierte en tarjetas. */
          <div className="pagos-table-wrapper">
            <table className="pagos-table pagos-libre">
              <thead>
                <tr>
                  {tabla.columnas.map((c) => (
                    // La cabecera lleva la misma clase que la celda: si el
                    // valor se va a la derecha, el título tiene que irse con él.
                    <th key={c} className={tipoDe(c) === "monto" ? "money" : undefined}>
                      {etiquetaCol(c)}
                    </th>
                  ))}
                  <th>Acciones</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={tabla.columnas.length + 1} className="empty">
                      Consultando...
                    </td>
                  </tr>
                ) : tabla.filas.length === 0 ? (
                  <tr>
                    <td colSpan={Math.max(tabla.columnas.length, 1) + 1} className="empty">
                      Sin resultados
                    </td>
                  </tr>
                ) : (
                  tabla.filas.map((f) => (
                    <tr key={f.id}>
                      {tabla.columnas.map((c) => {
                        const tipo = tipoDe(c);
                        const abierto = editandoReg(f.id, c);
                        return (
                          <td
                            key={c}
                            data-label={etiquetaCol(c)}
                            className={tipo === "monto" ? "money" : undefined}
                            onDoubleClick={() => abrirReg(f, c)}
                          >
                            {abierto ? inputReg(c) : verValor(c, f[c])}
                          </td>
                        );
                      })}

                      <td data-label="Acciones">
                        <AccionFila
                          icono={deletingId === f.id ? Iconos.cargando : Iconos.eliminar}
                          titulo={`Borrar registro #${f.id}`}
                          peligro
                          type="button"
                          disabled={loading || savingId === f.id || deletingId === f.id}
                          onClick={() => pedirBorrarReg(f)}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {savingId ? (
          <div className="pagos-saving">Guardando cambios en #{savingId}...</div>
        ) : null}

        {deletingId ? (
          <div className="pagos-saving">Borrando #{deletingId}...</div>
        ) : null}
      </div>

      <Confirmar
        abierto={!!porBorrar}
        titulo={porBorrar?.titulo}
        mensaje={porBorrar?.mensaje}
        aviso={porBorrar?.aviso}
        textoConfirmar="Sí, borrar"
        onConfirmar={confirmarBorrado}
        onCancelar={() => setPorBorrar(null)}
      />
    </div>
  );
}
